import React from 'react';
import MaskedMobile from '../../common/MaskedMobile';
import Select from '../../common/Select';
import Tooltip from '../../common/Tooltip';
import TableSkeleton from '../../common/TableSkeleton';
import { MarketingOrderRow } from './marketingOrdersView';
import { X } from 'lucide-react';
import MouCount from '../../common/MouCount';
import { staffFilterOption, staffFilterValue } from '../../common/StaffFilterOption';

interface Props {
  orders: MarketingOrderRow[];
  loading: boolean;
  page: number;
  pageSize: number;
  isOrgWide: boolean;
  isMarketingManager: boolean;
  marketingStaff: any[];
  farmStaff: any[];
  onboardedByMobile?: string;
  farmVisitByMobile?: string;
  withCpf?: string;
  search?: string;
  setOnboardedByMobile?: (v: string) => void;
  setFarmVisitByMobile?: (v: string) => void;
  setWithCpf?: (v: string) => void;
  adminMobile?: string;
  /**
   * Replaces the trailing Incentive / Slab columns — the Farm Orders tab
   * shows the farm incentive, its approval and the approve action there
   * instead, with every other column the same as Marketing Orders.
   */
  incentiveColumns?: OrderColumn[];
  emptyText?: string;
}

export interface OrderColumn {
  header: string;
  render: (o: MarketingOrderRow) => React.ReactNode;
  align?: 'left' | 'center';
}

export const MarketingOrdersTable: React.FC<Props> = ({
  orders, loading, page, pageSize, isOrgWide, isMarketingManager,
  marketingStaff, farmStaff, onboardedByMobile, farmVisitByMobile,
  withCpf, search, setOnboardedByMobile, setFarmVisitByMobile, setWithCpf, adminMobile,
  incentiveColumns, emptyText,
}) => {
  const trailing: OrderColumn[] = incentiveColumns || [
    {
      header: 'Incentive',
      render: (o) => (o.incentive?.amount ? (
        <div className="flex flex-col gap-0.5">
          <span className="font-black text-slate-800 text-[13px]">
            ₹{o.incentive.amount.toLocaleString('en-IN')}
          </span>
        </div>
      ) : (
        <span className="text-[11px] text-slate-400">—</span>
      )),
    },
    {
      header: 'Slab',
      render: (o) => <span className="text-[12px] text-slate-600">{o.incentive?.slab || '—'}</span>,
    },
  ];
  const colCount = 15 + trailing.length;

  const paymentStatusStyle = (s?: string) => {
    switch (s) {
      case 'PAID': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'PENDING_PAYMENT': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'PENDING_SUBMISSION': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'PENDING_ADMIN_VERIFICATION': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'PENDING_SUPER_ADMIN_VERIFICATION': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'PENDING_SUPER_ADMIN_REJECTION': return 'bg-rose-50 text-rose-700 border-rose-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const paymentStatusLabel = (s?: string) => (s || '').split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') || '—';

  const getOnboardedBy = (o: MarketingOrderRow) => {
    const mobile = o.unit_placed_by_mobile || o.order_onboarded_by_mobile || o.onboarded_by_mobile || o.incentive?.credited_to_mobile;
    let name = o.order_onboarded_by_name || o.onboarded_by_name || o.incentive?.credited_to_name;
    if (!name && mobile) {
      const staff = marketingStaff.find((s) => s.mobile === mobile);
      if (staff) {
        name = [staff.first_name, staff.last_name].filter(Boolean).join(' ');
      }
    }
    return { name, mobile };
  };

  const stripZoneId = (v: string) => v.replace(/\[[^\]]+\]$/, '');

  const fmtDate = (v?: string) => {
    if (!v) return '—';
    const dt = new Date(stripZoneId(v));
    if (isNaN(dt.getTime())) return v;
    return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
  };

  const fmtDateTime = (v?: string) => {
    if (!v) return '';
    const dt = new Date(stripZoneId(v));
    if (isNaN(dt.getTime())) return v;
    return `${fmtDate(v)}, ${dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const formatRawDateTime = (v?: string) => {
    if (!v) return '';
    const dt = new Date(stripZoneId(v));
    if (isNaN(dt.getTime())) return v;
    return fmtDateTime(v);
  };

  const visitDurationLabel = (mins?: number | null) => {
    if (typeof mins !== 'number') return '';
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const approvedByLine = (o: MarketingOrderRow) => {
    // This column has been removed as per user request.
    return null;
  };

  return (
    <div className="w-full">
      <div className="bg-white border border-slate-200 shadow-sm overflow-hidden overflow-x-auto rounded-2xl">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-slate-100 bg-slate-50/50">
              <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">S.No</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Order ID</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Investor</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Referred By</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                {setOnboardedByMobile ? (
                  <Select
                    value={onboardedByMobile || ''}
                    onChange={(v) => setOnboardedByMobile(v)}
                    options={[
                      { value: '', label: 'Lead By (All)' },
                      ...marketingStaff.map(staffFilterOption),
                    ]}
                    renderValue={staffFilterValue(marketingStaff)}
                    className="bg-transparent text-slate-500 font-semibold text-[11px] uppercase p-0 border-none min-w-[120px]"
                  />
                ) : 'Lead By'}
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                {setFarmVisitByMobile ? (
                  <Select
                    value={farmVisitByMobile || ''}
                    onChange={(v) => setFarmVisitByMobile(v)}
                    options={[
                      { value: '', label: 'Farm Executive (All)' },
                      ...farmStaff.map(staffFilterOption),
                    ]}
                    renderValue={staffFilterValue(farmStaff)}
                    className="bg-transparent text-slate-500 font-semibold text-[11px] uppercase p-0 border-none min-w-[120px]"
                  />
                ) : 'Farm Executive'}
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Units</th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" title="Units with the MOU given / units">MOU</th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '120px' }}>
                {setWithCpf ? (
                  <Select
                    value={withCpf || ''}
                    onChange={(v) => setWithCpf(v as '' | 'true' | 'false')}
                    options={[
                      { value: '', label: 'CPF (All)' },
                      { value: 'true', label: 'CPF (Yes)' },
                      { value: 'false', label: 'CPF (No)' },
                    ]}
                    className="bg-transparent text-slate-500 font-semibold text-[11px] uppercase p-0 border-none min-w-[100px]"
                  />
                ) : 'CPF'}
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Payment Status</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Total Cost</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Total Paid</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Approved At</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Lead Source</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Type</th>
              {trailing.map((c) => (
                <th key={c.header} className={`px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${c.align === 'center' ? 'text-center' : 'text-left'}`}>{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <TableSkeleton cols={colCount} rows={7} />
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="text-center py-16 text-sm text-slate-400">
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-4xl opacity-30">📋</span>
                    <span className="font-medium">
                      {emptyText || ((search || withCpf || onboardedByMobile || farmVisitByMobile)
                        ? 'No orders found matching your filters.'
                        : 'No paid orders yet.')}
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              orders.map((o, idx) => (
                <tr key={o.order_id} className="hover:bg-slate-50/70 transition-colors bg-white">
                  <td className="px-4 py-4 text-center text-slate-400 font-semibold text-[12px]">{(page - 1) * pageSize + idx + 1}</td>
                  <td className="px-4 py-4 font-mono text-[12px] text-slate-700 whitespace-nowrap">{o.order_id}</td>
                  <td className="px-4 py-4">
                    <div className="font-bold text-slate-800 text-[13px] whitespace-nowrap">{o.investor_name || '—'}</div>
                    <div className="text-slate-500 font-medium text-[12px]"><MaskedMobile mobile={o.investor_mobile} className="text-slate-500 font-medium text-[12px]" /></div>
                  </td>
                  <td className="px-4 py-4">
                    {(o.referred_by_name || o.referred_by_mobile) ? (
                      <div className="flex flex-col gap-0.5 whitespace-nowrap">
                        <span className="font-bold text-slate-800 text-[13px]">{o.referred_by_name || o.referred_by_mobile}</span>
                        {o.referred_by_mobile && (
                          <MaskedMobile mobile={o.referred_by_mobile} className="text-slate-500 font-medium text-[12px]" />
                        )}
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-4">
                    {(() => {
                      const ob = getOnboardedBy(o);
                      if (ob.name || ob.mobile) {
                        return (
                          <div className="flex flex-col gap-0.5 whitespace-nowrap">
                            <span className="font-bold text-slate-800 text-[13px]">{ob.name || ob.mobile}</span>
                            {ob.mobile && (
                              <div className="text-slate-500 font-medium text-[12px]">
                                <MaskedMobile mobile={ob.mobile} className="text-slate-500 font-medium text-[12px]" />
                              </div>
                            )}
                          </div>
                        );
                      }
                      return <span className="text-slate-300">—</span>;
                    })()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {o.farmvisit_by ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[13px] font-semibold text-slate-800">
                          {o.farmvisit_by.name || o.farmvisit_by.mobile}
                        </span>
                        <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">
                          {o.farmvisit_by.visit_type || 'Visit'}
                        </span>
                        {o.farmvisit_by.checked_in_at && (
                          <span className="text-[10px] text-slate-400">
                            {formatRawDateTime(o.farmvisit_by.checked_in_at)}
                            {' · '}
                            {visitDurationLabel(o.farmvisit_by.duration_minutes)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-center text-slate-600 font-medium text-[13px]">{o.numUnits}</td>
                  <td className="px-4 py-4 text-center">{o.paymentStatus === 'PAID' ? <MouCount mou={o.mou} /> : <span className="text-xs text-slate-400">—</span>}</td>
                  <td className="px-4 py-4 text-center whitespace-nowrap">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                      o.cpf_status?.toLowerCase() === 'yes' || o.with_cpf
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {o.cpf_status || (o.with_cpf ? 'Yes' : 'No')}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center whitespace-nowrap">
                    <span className={`inline-flex self-start px-2 py-0.5 rounded-lg border text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${paymentStatusStyle(o.paymentStatus)}`}>
                      {paymentStatusLabel(o.paymentStatus)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-slate-600 font-medium text-[13px]">₹{(o.totalCost || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-4 text-slate-600 font-medium text-[13px]">₹{(o.totalPaid || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-4 text-slate-600 font-medium text-[13px] whitespace-nowrap">{fmtDate(o.order_approved_date)}</td>
                  <td className="px-4 py-4">
                    {o.lead_source ? (
                      <span className="inline-flex self-start px-2 py-0.5 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                        {o.lead_source}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-4">
                    {o.incentive?.lead_type ? (
                      <span className={`inline-flex self-start px-2 py-0.5 rounded-lg border text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${
                        o.incentive.lead_type === 'DIRECT'
                          ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                          : 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700'
                      }`}>
                        {o.incentive.lead_type}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  {trailing.map((c) => (
                    <td key={c.header} className={`px-4 py-4 whitespace-nowrap ${c.align === 'center' ? 'text-center' : ''}`}>{c.render(o)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
